import React from "react";
import { cn } from "../../lib/cn";

const baseField = "w-full rounded-md bg-surface text-ink border transition-all duration-300 ease-smooth " + "placeholder:text-muted/70 outline-none " + "focus:ring-2 focus:ring-accent/20 disabled:opacity-60 disabled:cursor-not-allowed";

/**
 * Text input. Supports leftIcon / rightIcon (rendered inside the field) and error state.
 */
export const Input = React.forwardRef(function Input(
  { className, error, leftIcon, rightIcon, type = "text", ...props },
  ref
) {
  const field = (
    <input
      ref={ref}
      type={type}
      className={cn(
        baseField, "px-2.5 py-1.5 text-sm",
        error ? "border-danger focus:border-danger focus:ring-danger/20" : "border-line/15 focus:border-accent",
        leftIcon && "pl-11",
        rightIcon && "pr-11",
        className
      )}
      {...props}
    />
  );

  if (!leftIcon && !rightIcon) return field;

  return (
    <div className="relative">
      {leftIcon && (
        <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted">{leftIcon}</span>
      )}
      {field}
      {rightIcon && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted">{rightIcon}</span>
      )}
    </div>
  );
});

export const Textarea = React.forwardRef(function Textarea({ className, error, rows = 4, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(
        baseField, "px-2.5 py-1.5 text-sm resize-y",
        error ? "border-danger focus:border-danger focus:ring-danger/20" : "border-line/15 focus:border-accent",
        className
      )}
      {...props}
    />
  );
});

export const Select = React.forwardRef(function Select({ className, error, children, ...props }, ref) {
  return (
    <div className="relative">
      <select
        ref={ref}
        className={cn(
          baseField, "appearance-none px-2.5 py-1.5 pr-9 text-sm",
          error ? "border-danger focus:ring-danger/20" : "border-line/15 focus:border-accent",
          className
        )}
        {...props}
      >
        {children}
      </select>
      <svg
        className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-muted"
        width="14" height="14" viewBox="0 0 12 8" fill="none" aria-hidden="true"
      >
        <path d="M1 1l5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    </div>
  );
});

export default Input;
